import styled from 'styled-components';
import { motion } from 'framer-motion';

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
}

export const Button = styled(motion.button)<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ size, theme }) => {
    switch (size) {
      case 'small':
        return `${theme.spacing.sm} ${theme.spacing.md}`;
      case 'large':
        return `${theme.spacing.lg} ${theme.spacing.xl}`;
      default:
        return `${theme.spacing.md} ${theme.spacing.lg}`;
    }
  }};
  font-size: ${({ size, theme }) => {
    switch (size) {
      case 'small':
        return theme.fontSizes.sm;
      case 'large':
        return theme.fontSizes.lg;
      default:
        return theme.fontSizes.base;
    }
  }};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 2px solid transparent;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  min-height: 48px;
  -webkit-tap-highlight-color: transparent;

  ${({ variant, theme }) => {
    switch (variant) {
      case 'secondary':
        return `
          background-color: ${theme.colors.surface};
          color: ${theme.colors.onSurface};
          border-color: ${theme.colors.border};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colors.input};
          }
          
          &:active:not(:disabled) {
            transform: scale(0.98);
          }
        `;
      case 'outline':
        return `
          background-color: transparent;
          color: ${theme.colors.primary};
          border-color: ${theme.colors.primary};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colors.primary}10;
          }
          
          &:active:not(:disabled) {
            transform: scale(0.98);
          }
        `;
      default:
        return `
          background-color: ${theme.colors.primary};
          color: white;
          
          &:hover:not(:disabled) {
            background-color: ${theme.colors.primaryDark};
          }
          
          &:active:not(:disabled) {
            transform: scale(0.98);
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.primary}40;
    outline-offset: 2px;
  }
`;

Button.defaultProps = {
  variant: 'primary',
  size: 'medium',
  fullWidth: false,
  whileTap: { scale: 0.98 },
};
